import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Lock } from "lucide-react";
import FloralAccent from "@/components/FloralAccent";

/**
 * PasswordGate - a soft romantic lock screen that guards the site.
 * The correct answer is stored in `SECRET` (client-side only — this is a
 * gift, not a bank vault ♡). Once unlocked, we remember it in localStorage
 * so Lakshita doesn't have to enter it on every visit.
 */
const SECRET = "mootu";
const STORAGE_KEY = "lakshita-gate-unlocked";

export const PasswordGate = ({ children }) => {
    const [unlocked, setUnlocked] = useState(false);
    const [value, setValue] = useState("");
    const [wrong, setWrong] = useState(false);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        try {
            const already = localStorage.getItem(STORAGE_KEY) === "yes";
            if (already) setUnlocked(true);
        } catch {}
        setReady(true);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const guess = value.trim().toLowerCase();
        if (guess === SECRET) {
            try {
                localStorage.setItem(STORAGE_KEY, "yes");
            } catch {}
            setUnlocked(true);
        } else {
            setWrong(true);
            setTimeout(() => setWrong(false), 900);
        }
    };

    if (!ready) return null;

    return (
        <>
            <AnimatePresence>
                {!unlocked && (
                    <motion.div
                        key="gate"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.8 } }}
                        className="fixed inset-0 z-[100] flex items-center justify-center px-6 paper-texture bg-cream"
                        data-testid="password-gate"
                    >
                        <FloralAccent
                            variant="sprig"
                            className="hidden sm:block absolute -top-4 -left-6 w-52 opacity-70 animate-sway"
                        />
                        <FloralAccent
                            variant="wildflower"
                            className="hidden sm:block absolute top-10 right-4 w-40 opacity-80 animate-float-slow"
                        />
                        <FloralAccent
                            variant="rose"
                            className="hidden sm:block absolute bottom-10 right-10 w-44 opacity-80 animate-float-slow"
                        />
                        <FloralAccent
                            variant="leaves"
                            className="hidden md:block absolute bottom-6 left-6 w-36 opacity-70"
                        />

                        <motion.div
                            initial={{ opacity: 0, y: 24, scale: 0.96 }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                scale: 1,
                                transition: {
                                    duration: 0.9,
                                    ease: [0.22, 1, 0.36, 1],
                                },
                            }}
                            className="relative w-full max-w-lg bg-ivory border border-dusty rounded-3xl px-8 sm:px-12 py-12 letter-shadow text-center"
                        >
                            <div className="mx-auto w-14 h-14 rounded-full bg-dusty-rose flex items-center justify-center shadow-md">
                                <Lock className="w-6 h-6 text-white" />
                            </div>

                            <p
                                className="mt-6 font-hand text-2xl text-dusty-rose-deep"
                                data-testid="gate-eyebrow"
                            >
                                a little locked garden ♡
                            </p>
                            <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-cocoa leading-tight">
                                For{" "}
                                <span className="italic text-dusty-rose-deep">
                                    Lakshita
                                </span>
                                's eyes only.
                            </h1>
                            <p className="mt-5 font-body text-cocoa-soft leading-relaxed">
                                Hi love. I built this whole little corner just
                                for you. Whisper our tiny secret word to come
                                inside — you already know it{" "}
                                <span className="font-hand text-xl text-dusty-rose-deep">
                                    ♡
                                </span>
                            </p>

                            <form
                                onSubmit={handleSubmit}
                                className="mt-8 flex flex-col items-center gap-4"
                                data-testid="gate-form"
                            >
                                <motion.input
                                    type="password"
                                    inputMode="text"
                                    autoFocus
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    placeholder="our little word"
                                    data-testid="gate-input"
                                    animate={
                                        wrong
                                            ? {
                                                  x: [0, -10, 10, -8, 8, -4, 4, 0],
                                              }
                                            : { x: 0 }
                                    }
                                    transition={{ duration: 0.5 }}
                                    className={`w-full max-w-xs text-center px-5 py-3 rounded-full bg-white border ${
                                        wrong
                                            ? "border-destructive"
                                            : "border-dusty"
                                    } focus:outline-none focus:ring-2 focus:ring-dusty-rose font-serif text-lg text-cocoa tracking-widest`}
                                />
                                <button
                                    type="submit"
                                    data-testid="gate-submit"
                                    className="inline-flex items-center gap-2 rounded-full bg-dusty-rose text-white px-8 py-3 font-serif text-lg tracking-wide hover:bg-dusty-rose-deep transition-colors shadow-lg shadow-dusty-rose/30"
                                >
                                    <Heart
                                        className="w-4 h-4"
                                        fill="white"
                                    />
                                    Open my letter
                                </button>
                                <p
                                    data-testid="gate-hint"
                                    className={`font-hand text-xl h-6 transition-opacity ${
                                        wrong
                                            ? "opacity-100 text-destructive"
                                            : "opacity-70 text-cocoa-light"
                                    }`}
                                >
                                    {wrong
                                        ? "not quite, my love — try again ♡"
                                        : "hint: the one only I call you"}
                                </p>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {unlocked && children}
        </>
    );
};

export default PasswordGate;
